import React, { useState, useEffect } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Textarea } from "@cms/ui/components/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@cms/ui/components/dialog";
import { ImageField } from "./image-field";
import type { ApiClient } from "../lib/api";
import {
  priceToInput, parsePriceInput, tagLabel, ALLERGEN_LABELS, DIETARY_LABELS,
  type ItemFields, type MenuItem,
} from "../lib/menu-module";

/** An existing item being edited, with its resolved image URL for preview. */
export type MenuItemDialogInitial = MenuItem & { imageUrl?: string | null };

interface ItemForm {
  name: string;
  description: string;
  price: string;
  allergens: string[];
  dietary_tags: string[];
  image_r2_key: string | null;
  imageUrl: string | null;
  variants: { name: string; price: string }[];
}

const EMPTY_FORM: ItemForm = {
  name: "",
  description: "",
  price: "",
  allergens: [],
  dietary_tags: [],
  image_r2_key: null,
  imageUrl: null,
  variants: [],
};

function CheckboxGrid({ legend, options, labels, selected, onToggle }: {
  legend: string;
  options: string[];
  labels: Record<string, string>;
  selected: string[];
  onToggle: (slug: string) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-medium">{legend}</legend>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {options.map((slug) => (
          <label key={slug} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={selected.includes(slug)}
              onChange={() => onToggle(slug)}
            />
            {tagLabel(slug, labels)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The shared create/edit dialog for library items, used by the Items page
 * and the menu editor's inline add. `initial` is null for create; pass
 * { ...item, imageUrl } for edit. onSave receives normalised ItemFields.
 */
export function MenuItemDialog({
  open, onClose, onSave, initial, allergens, dietaryTags, api, saving, error,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (fields: ItemFields) => void;
  initial: MenuItemDialogInitial | null;
  allergens: string[];
  dietaryTags: string[];
  api: ApiClient | null;
  saving: boolean;
  error: string;
}) {
  const [form, setForm] = useState<ItemForm>(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setFormError("");
    setForm(initial ? {
      name: initial.name,
      description: initial.description ?? "",
      price: priceToInput(initial.price_pence),
      allergens: initial.allergens ?? [],
      dietary_tags: initial.dietary_tags ?? [],
      image_r2_key: initial.image_r2_key ?? null,
      imageUrl: initial.imageUrl ?? null,
      variants: (initial.variants ?? []).map((variant) => ({
        name: variant.name,
        price: priceToInput(variant.price_pence),
      })),
    } : EMPTY_FORM);
  }, [open, initial]);

  const toggleTag = (field: "allergens" | "dietary_tags") => (slug: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(slug)
        ? prev[field].filter((tag) => tag !== slug)
        : [...prev[field], slug],
    }));
  };

  const setVariant = (index: number, variantPatch: Partial<{ name: string; price: string }>) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, i) => (i === index ? { ...variant, ...variantPatch } : variant)),
    }));
  };

  const save = () => {
    const pence = parsePriceInput(form.price);
    if (pence === false) {
      setFormError("Enter a price like 12.50, or leave it empty.");
      return;
    }
    const variants: { name: string; price_pence: number | null }[] = [];
    for (const variant of form.variants) {
      const name = variant.name.trim();
      if (!name) {
        setFormError("Every variant needs a name (e.g. Small, Large).");
        return;
      }
      const variantPence = parsePriceInput(variant.price);
      if (variantPence === false) {
        setFormError(`"${name}" has an invalid price — use 12.50, or leave it empty.`);
        return;
      }
      variants.push({ name, price_pence: variantPence });
    }
    setFormError("");
    onSave({
      name: form.name.trim(),
      description: form.description.trim() || null,
      price_pence: pence,
      allergens: form.allergens,
      dietary_tags: form.dietary_tags,
      image_r2_key: form.image_r2_key,
      variants,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit item" : "Add an item"}</DialogTitle>
          <DialogDescription>
            Items live in one shared list — add this dish to as many menus as you like.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="menu-item-name">Name</Label>
            <Input
              id="menu-item-name"
              value={form.name}
              maxLength={100}
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Slow-roasted lamb shank"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="menu-item-description">Description</Label>
            <Textarea
              id="menu-item-description"
              value={form.description}
              maxLength={500}
              rows={2}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="A line or two your customers will read"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="menu-item-price">Price (£)</Label>
            <Input
              id="menu-item-price"
              value={form.price}
              inputMode="decimal"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, price: e.target.value }))}
              placeholder="12.50 — leave empty for no price"
              className="max-w-40"
            />
            {form.variants.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Used for any variant below that has no price of its own.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Variants (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Sizes or flavours — e.g. Small / Large, or Vanilla / Strawberry / Chocolate —
              each with its own price if needed.
            </p>
            {form.variants.map((variant, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={variant.name}
                  maxLength={50}
                  placeholder="e.g. Large"
                  aria-label={`Variant ${index + 1} name`}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVariant(index, { name: e.target.value })}
                />
                <Input
                  value={variant.price}
                  inputMode="decimal"
                  placeholder="£"
                  aria-label={`Variant ${index + 1} price`}
                  className="max-w-24"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVariant(index, { price: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove variant ${index + 1}`}
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    variants: prev.variants.filter((_, i) => i !== index),
                  }))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={form.variants.length >= 20}
              onClick={() => setForm((prev) => ({
                ...prev,
                variants: [...prev.variants, { name: "", price: "" }],
              }))}
            >
              <Plus className="h-4 w-4" />
              Add variant
            </Button>
          </div>

          <CheckboxGrid
            legend="Allergens (UK mandatory 14)"
            options={allergens}
            labels={ALLERGEN_LABELS}
            selected={form.allergens}
            onToggle={toggleTag("allergens")}
          />

          <CheckboxGrid
            legend="Dietary"
            options={dietaryTags}
            labels={DIETARY_LABELS}
            selected={form.dietary_tags}
            onToggle={toggleTag("dietary_tags")}
          />

          <div className="space-y-1.5">
            <Label>Photo (optional)</Label>
            <ImageField
              api={api}
              value={form.image_r2_key ? { key: form.image_r2_key, url: form.imageUrl } : null}
              onChange={(item) => setForm((prev) => ({
                ...prev,
                image_r2_key: item?.key ?? null,
                imageUrl: item?.url ?? null,
              }))}
              emptyLabel="No photo"
              chooseLabel="Add photo"
              replaceLabel="Replace photo"
            />
          </div>

          {(formError || error) && <p className="text-sm text-destructive">{formError || error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {initial ? "Save item" : "Add item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
